import moment from "moment";
import posthog from "posthog-js";
import { WeekEnds, DateFormat } from "../../../../Global/Constant";

// Helper function to check if a day is a weekend based on officeBusinessDays
const isWeekend = (date, officeBusinessDays) => {
  // Validate inputs
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    console.warn('isWeekend: Invalid date parameter:', date);
    return false;
  }

  if (!officeBusinessDays || typeof officeBusinessDays !== 'number' || isNaN(officeBusinessDays)) {
    // Default to standard weekend (Saturday and Sunday) if no valid value provided
    return date.getDay() === WeekEnds.Saturday || date.getDay() === WeekEnds.Sunday;
  }

  // Get the day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = date.getDay();

  // Create a bit mask for the current day
  // Sunday = 1 (2^0), Monday = 2 (2^1), Tuesday = 4 (2^2), etc.
  const dayBitMask = 1 << dayOfWeek;

  // Check if the day is a business day by checking if the bit is set
  // If the bit is 0, it's a weekend; if it's 1, it's a business day
  return (officeBusinessDays & dayBitMask) === 0;
};

// Helper function to check if a leave has pending approvals
const hasPendingApprovals = (leave) => {
  if (!leave || !leave.approvals || !Array.isArray(leave.approvals)) {
    return false;
  }

  // Check if any approval has status 0 (Pending)
  return leave.approvals.some(approval => approval.approvalStatus === 0);
};

// Helper function to get the appropriate color based on approval status
const getLeaveColor = (leave, defaultColor = '#00B4D8') => {
  if (hasPendingApprovals(leave)) {
    return '#efe3d5'; // Gray color for pending approvals
  }
  return leave.color || defaultColor;
};

const current = {
  date: moment(),
  classNames: ["current"],
  component: ["day"],
};
const bindEvents = (events, date) => {
  const boundEvents = {};

  if (!events) return null;

  Object.keys(events).forEach(
    (key) => (boundEvents[key] = events[key].bind(null, date))
  );

  return boundEvents;
};

// const getClsMods = (clsPrefix, mods) =>
//   !mods || !mods.classNames
//     ? null
//     : mods.classNames.map((cls) => `${clsPrefix}--${cls}`);

const getClsMods = (clsPrefix, mods) => {
  if (!mods || !mods.classNames) {
    return null;
  } else {
    return mods.classNames.map((cls) => `${clsPrefix}--${cls}`);
  }
};
/**
 * Internal: Creates a single modifier object for a date
 */
const getModByDate = (mods, date, type) => {
  const modifier = {
    date: null,
    classNames: [],
    events: {},
    style: {},
    id: null, // Add id property
    secondaryId: null, // Add secondaryId property
    groupFirstHalf: null, // Add groupFirstHalf property
    groupSecondHalf: null, // Add groupSecondHalf property
    firstHalfEntry: null, // Add firstHalfEntry property
    secondHalfEntry: null, // Add secondHalfEntry property
    firstHalfGroupId: null, // Add firstHalfGroupId property
    secondHalfGroupId: null, // Add secondHalfGroupId property
    isGroup: null, // Add isGroup property
    leaveType: null, // Add leaveType property
    specialGroupId: null, // Add specialGroupId property
  };


  mods
    .filter((mod) => (mod.date ? mod.date.isSame(date, type) : null))
    .forEach((_mod) => {
      modifier.date = _mod.date;
      modifier.events = _mod.events;
      if (Array.isArray(_mod.classNames)) {
        modifier.classNames.push(..._mod.classNames);
      }
      // Handle style property
      if (_mod.style) {
        modifier.style = { ...modifier.style, ..._mod.style };
      }
      // Handle id property
      if (_mod.id) {
        modifier.id = _mod.id;
      }
      // Handle secondaryId property
      if (_mod.secondaryId) {
        modifier.secondaryId = _mod.secondaryId;
      }
      // Handle groupFirstHalf property
      if (_mod.groupFirstHalf) {
        modifier.groupFirstHalf = _mod.groupFirstHalf;
      }
      // Handle groupSecondHalf property
      if (_mod.groupSecondHalf) {
        modifier.groupSecondHalf = _mod.groupSecondHalf;
      }
      // Handle firstHalfEntry property
      if (_mod.firstHalfEntry) {
        modifier.firstHalfEntry = _mod.firstHalfEntry;
      }
      // Handle secondHalfEntry property
      if (_mod.secondHalfEntry) {
        modifier.secondHalfEntry = _mod.secondHalfEntry;
      }
      // Handle firstHalfGroupId property
      if (_mod.firstHalfGroupId) {
        modifier.firstHalfGroupId = _mod.firstHalfGroupId;
      }
      // Handle secondHalfGroupId property
      if (_mod.secondHalfGroupId) {
        modifier.secondHalfGroupId = _mod.secondHalfGroupId;
      }
      // Handle isGroup property
      if (_mod.isGroup !== undefined) {
        modifier.isGroup = _mod.isGroup;
      }
      // Handle leaveType property
      if (_mod.leaveType) {
        modifier.leaveType = _mod.leaveType;
      }
      // Handle specialGroupId property
      if (_mod.specialGroupId) {
        modifier.specialGroupId = _mod.specialGroupId;
      }
    });

  return modifier;
};

const getModsWithDateRange = (mods) => mods.filter((mod) => !!mod.startDate);

const explodeDateRanges = (mods) => {
  return mods
    .map((mod) => {
      const diff = mod.endDate.diff(mod.startDate, "days");

      if (!diff) {
        // if the diff is 0 just return the mod
        mod.date = mod.startDate.clone();
        return mod;
      }
      return Array(diff)
        .fill(mod)
        .map((mod, i) => {
          return {
            ...mod,
            date: mod.startDate.clone().add(i, "days"),
          };
        });
    })
    .reduce((a, b) => a.concat(b), []);
};
//use later
// const getModsWithSingleDate = mods =>
//   mods.filter(mod => !mod.startDate && mod.date);

const getModsWithoutDate = (mods) => mods.filter((mod) => !mod.date);

export const getModsByCompType = (componentType, mods) => {
  if (!Array.isArray(mods)) {
    return [];
  }
  return mods.filter(
    ({ component }) => component?.indexOf(componentType.toLowerCase()) > -1
  );
};
const getDate = (date) => {
  let convertedDate = date.split("T")[0];
  return convertedDate;
};
export const getLabel = (i) => {
  let monthLabel = [
    "Jan",
    "Feb",
    "March",
    "April",
    "May",
    "June",
    "July",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return monthLabel[i];
};
export const GroupByMonth = (data) => {
  let groups = [[], [], [], [], [], [], [], [], [], [], [], []],
    itemGroupedByMonths = [],
    monthStartIndex;

  if (data.length > 0) {
    data.forEach((element, index) => {
      monthStartIndex = new Date(element.startDate).getMonth();
      groups[monthStartIndex].push(element);
    });

    for (let i = 0; i < groups.length; i++) {
      if (groups[i].length) {
        itemGroupedByMonths.push({
          month: getLabel(i),
          items: groups[i],
        });
      }
    }
  }

  return itemGroupedByMonths;
};

export const getDayModsByCompType = (
  componentType,
  startDate,
  employeeLeaves,
  onFirstHalfClick,
  employeeId,
  publicHolidays,
  officeBusinessDays,
  isEmployeeViewable = true
) => {
  // Validate inputs
  if (!startDate || !startDate.month || typeof startDate.month !== 'function') {
    console.warn('getDayModsByCompType: Invalid startDate parameter:', startDate);
    return [];
  }

  var mods = [];
  const selectedMonthWithYear = `${startDate.month()}-${startDate.year()}`;
  const currentMonthWithYear = `${current.date.month()}-${current.date.year()}`;
  if (selectedMonthWithYear === currentMonthWithYear) {
    mods.push(current);

    let date = new Date(),
      y = date.getFullYear(),
      m = date.getMonth();
    let firstDay = new Date(y, m, 1);
    let lastDay = new Date(y, m + 1, 0);

    // Validate the date objects before calling setEvents
    if (firstDay && lastDay && !isNaN(firstDay.getTime()) && !isNaN(lastDay.getTime())) {
      setEvents(
        firstDay,
        lastDay,
        onFirstHalfClick,
        employeeId,
        publicHolidays,
        employeeLeaves,
        mods,
        officeBusinessDays,
        isEmployeeViewable
      );
    } else {
      console.warn('getDayModsByCompType: Invalid date objects created:', { firstDay, lastDay });
    }
  } else {
    const obj = {
      date: startDate,
      component: ["day"],
    };
    mods.push(obj);
    const y = startDate.year();
    const m = startDate.month();
    let firstDay = new Date(y, m, 1);
    let lastDay = new Date(y, m + 1, 0);

    // Validate the date objects before calling setEvents
    if (firstDay && lastDay && !isNaN(firstDay.getTime()) && !isNaN(lastDay.getTime())) {
      setEvents(
        firstDay,
        lastDay,
        onFirstHalfClick,
        employeeId,
        publicHolidays,
        employeeLeaves,
        mods,
        officeBusinessDays,
        isEmployeeViewable
      );
    } else {
      console.warn('getDayModsByCompType: Invalid date objects created:', { firstDay, lastDay });
    }
  }

  return mods.filter(
    ({ component }) => component.indexOf(componentType.toLowerCase()) > -1
  );
};

// New function for year-wide processing
export const getYearDayModsByCompType = (
  componentType,
  startDate,
  employeeLeaves,
  onFirstHalfClick,
  employeeId,
  publicHolidays,
  officeBusinessDays,
  isEmployeeViewable = true
) => {
  // Validate inputs
  if (!startDate || !startDate.year || typeof startDate.year !== 'function') {
    console.warn('getYearDayModsByCompType: Invalid startDate parameter:', startDate);
    return [];
  }

  var mods = [];

  // Add current day if it's the current year
  const currentYear = new Date().getFullYear();
  const startYear = startDate.year();
  if (startYear === currentYear) {
    mods.push(current);
  }

  // Process the entire year from January 1st to December 31st
  const yearStartDate = new Date(startYear, 0, 1);
  const yearEndDate = new Date(startYear, 11, 31);

  // Validate the date objects before calling setEvents
  if (yearStartDate && yearEndDate && !isNaN(yearStartDate.getTime()) && !isNaN(yearEndDate.getTime())) {
    setEvents(
      yearStartDate,
      yearEndDate,
      onFirstHalfClick,
      employeeId,
      publicHolidays,
      employeeLeaves,
      mods,
      officeBusinessDays,
      isEmployeeViewable
    );
  } else {
    console.warn('getYearDayModsByCompType: Invalid date objects created:', { yearStartDate, yearEndDate });
  }

  return mods.filter(
    ({ component }) => component.indexOf(componentType.toLowerCase()) > -1
  );
};

export const getCustomModsByCompType = (
  componentType,
  mods,
  data,
  handleOpenModal,
  officeBusinessDays
) => {
  // Validate inputs
  if (!data || typeof data !== 'object') {
    console.warn('getCustomModsByCompType: Invalid data parameter:', data);
    return [];
  }

  let groupData,
    levaeMods,
    weekendMods,
    dayMods,
    startDate = new Date(new Date().getFullYear(), 0, 1),
    endDate = new Date(new Date().getFullYear(), 11, 31);
  mods = [];
  mods.push(current);
  if (data.empLeaves) groupData = GroupByMonth(data.empLeaves);

  // Validate the date objects before using them
  if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
    for (let i = startDate; i <= endDate; i.setDate(i.getDate() + 1)) {
      if (isWeekend(new Date(i), officeBusinessDays)) {
        weekendMods = {
          date: moment(new Date(i)),
          classNames: ["event"],
          component: ["day"],
        };
        mods.push(weekendMods);
      } else {
        dayMods = {
          date: moment(new Date(i)),
          component: ["day"],
          events: {
            onClick: (date, e) => handleOpenModal(new Date(), 0),
          },
        };
        mods.push(dayMods);
      }
    }
  } else {
    console.warn('getCustomModsByCompType: Invalid date objects created:', { startDate, endDate });
  }

  if (groupData?.length > 0) {
    let startDate, endDate;
    for (let i = 0; i < groupData.length; i++) {
      groupData[i].items.forEach((element, index) => {
        startDate = new Date(getDate(element.startDate));
        endDate = new Date(getDate(element.endDate));
        levaeMods = {
          startDate: moment(startDate, DateFormat.DDMMYYY),
          endDate: moment(endDate, DateFormat.DDMMYYY),
          classNames: ["warning"],
          component: ["day"],
          events: {
            onClick: (date, e) =>
              handleOpenModal(new Date(), element.userId),
          },
        };
        mods.push(levaeMods);
      });
    }
  }
  return mods.filter(
    ({ component }) => component.indexOf(componentType.toLowerCase()) > -1
  );
};

export const getMods = (mods, date, clsPrefix, type) => {
  if (!mods) {
    return null;
  }

  const events = {};
  const exploded = explodeDateRanges(getModsWithDateRange(mods));
  const mod = getModByDate([...mods, ...exploded], date, type);
  const clsMods = getClsMods(clsPrefix, mod) || [];

  // Handle getModsWithoutDate properly - it returns an array, so we need to process each mod
  const modsWithoutDate = getModsWithoutDate(mods);
  const clsCompMods = [];

  modsWithoutDate.forEach(modWithoutDate => {
    const compMods = getClsMods(clsPrefix, modWithoutDate);
    if (compMods) {
      clsCompMods.push(...compMods);
    }
  });

  getModsWithoutDate(mods).forEach((mod) =>
    Object.assign(events, bindEvents(mod.events, date))
  );

  if (mod && mod.events) {
    Object.assign(events, bindEvents(mod.events, date));
  }

  // Get styles from the mod
  const styles = mod && mod.style ? mod.style : {};

  // Get id from the mod
  const id = mod && mod.id ? mod.id : null;

  // Get secondary information for combined entries
  const secondaryId = mod && mod.secondaryId ? mod.secondaryId : null;
  const groupFirstHalf = mod && mod.groupFirstHalf ? mod.groupFirstHalf : null;
  const groupSecondHalf = mod && mod.groupSecondHalf ? mod.groupSecondHalf : null;
  const firstHalfEntry = mod && mod.firstHalfEntry ? mod.firstHalfEntry : null;
  const secondHalfEntry = mod && mod.secondHalfEntry ? mod.secondHalfEntry : null;
  const isGroup = mod && mod.isGroup ? mod.isGroup : null;
  // Get half-day specific group IDs
  const firstHalfGroupId = mod && mod.firstHalfGroupId ? mod.firstHalfGroupId : null;
  const secondHalfGroupId = mod && mod.secondHalfGroupId ? mod.secondHalfGroupId : null;
  const leaveType = mod && mod.leaveType ? mod.leaveType : null;
  const specialGroupId = mod && mod.specialGroupId ? mod.specialGroupId : null;
  return {
    clsMods: [...clsMods, ...clsCompMods],
    events,
    styles,
    id,
    secondaryId,
    isGroup,
    firstHalfEntry,
    secondHalfEntry,
    firstHalfGroupId,
    secondHalfGroupId,
    groupFirstHalf,
    groupSecondHalf,
    leaveType,
    specialGroupId,
  };
};

function setEvents(
  firstDay,
  lastDay,
  onFirstHalfClick,
  employeeId,
  publicHolidays,
  employeeLeaves,
  mods,
  officeBusinessDays,
  isEmployeeViewable = true,
) {
  // Basic validation
  if (!firstDay || !lastDay || !(firstDay instanceof Date) || !(lastDay instanceof Date)) {
    console.warn('setEvents: Invalid date parameters:', { firstDay, lastDay });
    return;
  }

  if (isNaN(firstDay.getTime()) || isNaN(lastDay.getTime())) {
    console.warn('setEvents: Invalid date values:', { firstDay, lastDay });
    return;
  }

  // Validate officeBusinessDays parameter
  if (officeBusinessDays !== undefined && (typeof officeBusinessDays !== 'number' || isNaN(officeBusinessDays))) {
    console.warn('setEvents: Invalid officeBusinessDays parameter:', officeBusinessDays);
    officeBusinessDays = undefined; // Reset to undefined to use default weekend logic
  }

  for (let i = firstDay; i <= lastDay; i.setDate(i.getDate() + 1)) {
    let dayMods = {
      date: moment(new Date(i)),
      component: ["day"],
      isGroup: false,
      events: {},
    };

    // Check if employee is viewable first - if not, disable all interactions
    if (isEmployeeViewable === false) {
      dayMods.events = {
        onClick: (date, date2, e, groupId, isLeftHalf) => {
          console.warn('Permission denied: User cannot view/edit this employee\'s leaves');
        },
        onMouseMove: (date, e, event) => {
          // Disable hover effects for non-viewable employee's leaves
          if (event && event.currentTarget && event.currentTarget.style) {
            event.currentTarget.style.cursor = 'default';
          }
        },
        onMouseLeave: (date, e, event) => {
          // Disable hover effects for non-viewable employee's leaves
          if (event && event.currentTarget && event.currentTarget.style) {
            event.currentTarget.style.cursor = 'default';
          }
        }
      };
      // Add visual indicator
      dayMods.classNames = dayMods.classNames || [];
      dayMods.classNames.push('leave-not-editable');
      dayMods.style = {
        ...dayMods.style,
        cursor: 'default'
      };
    } else {
      // Only set normal events if employee is viewable
      dayMods.events = {
        // Add back the onClick handler but make it work with our new half-specific approach
        onClick: (date, date2, e, groupId, isLeftHalf) => {
          // This handler will be called by the Day.js component's half-specific handlers
          // The groupId parameter will be either the specific half's group ID or null for adding new leaves
          try {
            console.warn('util.js onClick called with:', {
              date: date.format('YYYY-MM-DD'),
              groupId,
              employeeId,
              isLeftHalf,
              eventType: e ? 'click' : 'unknown'
            });

            // Use the groupId parameter passed from Day.js directly
            // This ensures we get the correct ID for the specific half that was clicked
            onFirstHalfClick(date, employeeId, groupId);
          } catch (err) {
            if (typeof posthog !== "undefined" && posthog.capture) {
              posthog.capture("timeOffCalendarOnClick", {
                type: "exception",
                success: false,
                reason: err?.message ?? "Error in onClick handler",
              });
            }
            // Fallback if anything goes wrong
            onFirstHalfClick(date, employeeId, null);
          }
        },
      };
    }

    if (isWeekend(new Date(i), officeBusinessDays)) {
      dayMods.classNames = ["event"];
    } else {
      var publicHoliday = publicHolidays?.find(
        (a) =>
          new Date(a.date).getDate() + "" + new Date(a.date).getMonth() ===
          i.getDate() + "" + i.getMonth()
      );
      if (publicHoliday !== null && publicHoliday !== undefined) {
        dayMods.classNames = ["event"];
      } else {
        // Find leaves that apply to this day
        const leavesCoveringDate = Array.isArray(employeeLeaves)
          ? employeeLeaves.filter((a) => {
            const leaveStartDate = new Date(a.startDate);
            const leaveEndDate = new Date(a.endDate);
            const currentDate = new Date(i.getFullYear(), i.getMonth(), i.getDate());
            const startDate = new Date(leaveStartDate.getFullYear(), leaveStartDate.getMonth(), leaveStartDate.getDate());
            const endDate = new Date(leaveEndDate.getFullYear(), leaveEndDate.getMonth(), leaveEndDate.getDate());
            return currentDate >= startDate && currentDate <= endDate;
          })
          : [];

        // Special case: same day has two separate entries (0,0) and (1,1), likely different groups/types
        if (leavesCoveringDate && leavesCoveringDate.length >= 2) {
          const curr = new Date(i.getFullYear(), i.getMonth(), i.getDate());
          const isSameDay = (d) => {
            const sd = new Date(new Date(d.startDate).getFullYear(), new Date(d.startDate).getMonth(), new Date(d.startDate).getDate());
            const ed = new Date(new Date(d.endDate).getFullYear(), new Date(d.endDate).getMonth(), new Date(d.endDate).getDate());
            return sd.getTime() === curr.getTime() && ed.getTime() === curr.getTime();
          };
          const sameDayEntries = leavesCoveringDate.filter(isSameDay);
          const firstHalfEntry = sameDayEntries.find(l => l.startingLeave === 0 && l.endingLeave === 0);
          const secondHalfEntry = sameDayEntries.find(l => l.startingLeave === 1 && l.endingLeave === 1);

          if (firstHalfEntry && secondHalfEntry) {
            // Enhanced special case handling: Check if entries belong to the same group
            // We need to figure out the group type by mapping through employeeLeaves
            // to find actual group matches, not just checking if group property exists

            // Map through employeeLeaves to find all entries with groups
            const allGroupEntries = employeeLeaves.filter(leave => leave.group);

            // Check if firstHalfEntry has a group that matches with other entries
            const firstHalfGroupMatches = allGroupEntries.filter(leave =>
              leave.group === firstHalfEntry.group &&
              leave !== firstHalfEntry
            );

            // Check if secondHalfEntry has a group that matches with other entries
            const secondHalfGroupMatches = allGroupEntries.filter(leave =>
              leave.group === secondHalfEntry.group &&
              leave !== secondHalfEntry
            );

            // Determine if entries actually have groups (not just group property)
            const hasFirstHalfGroup = firstHalfEntry.group && firstHalfGroupMatches.length > 0;
            const hasSecondHalfGroup = secondHalfEntry.group && secondHalfGroupMatches.length > 0;
            const hasGroup = hasFirstHalfGroup || hasSecondHalfGroup;

            // Check if both entries belong to the same group
            const isSameGroup = hasFirstHalfGroup && hasSecondHalfGroup && firstHalfEntry.group === secondHalfEntry.group;


            if (hasGroup) {
              // Check which entry has the group and handle accordingly
              if (isSameGroup) {
                // Same group: Create a consolidated mod with group styling
                dayMods.classNames = ["halfLeftBook", "halfRightBook", "dynamic-color", "special-case-same-group"];
                dayMods.id = firstHalfEntry.group; // Use the group ID
                dayMods.isGroup = true; // Mark as group for proper styling
                dayMods.firstHalfEntry = firstHalfEntry; // Store first entry data
                dayMods.secondHalfEntry = secondHalfEntry; // Store second entry data
              } else if (hasFirstHalfGroup && !hasSecondHalfGroup) {
                // Only first half has group
                dayMods.classNames = ["halfLeftBook", "halfRightBook", "dynamic-color", "special-case-first-half-group"];
                dayMods.id = firstHalfEntry.group;
                dayMods.isGroup = true;
                dayMods.firstHalfEntry = firstHalfEntry;
                dayMods.secondaryId = secondHalfEntry.group;
                dayMods.secondHalfEntry = secondHalfEntry;
                dayMods.groupFirstHalf = firstHalfEntry.group;
                dayMods.groupSecondHalf = secondHalfEntry.group;
              } else if (!hasFirstHalfGroup && hasSecondHalfGroup) {
                // Only second half has group
                dayMods.classNames = ["halfLeftBook", "halfRightBook", "dynamic-color", "special-case-second-half-group"];
                dayMods.id = firstHalfEntry.group;
                dayMods.isGroup = true;
                dayMods.secondaryId = secondHalfEntry.group;
                dayMods.firstHalfEntry = firstHalfEntry;
                dayMods.secondHalfEntry = secondHalfEntry;
                dayMods.groupFirstHalf = firstHalfEntry.group;
                dayMods.groupSecondHalf = secondHalfEntry.group;
              } else {
                // Both have groups but different ones - fallback to different groups logic
                dayMods.classNames = ["halfLeftBook", "halfRightBook", "dynamic-color", "special-case-combined"];
                dayMods.id = firstHalfEntry.group;
                dayMods.secondaryId = secondHalfEntry.group;
                dayMods.firstHalfEntry = firstHalfEntry;
                dayMods.secondaryHalfEntry = secondHalfEntry;
                dayMods.isGroup = false;

                // Check if both entries have groups and add groupId if found
                if (firstHalfEntry.group && secondHalfEntry.group) {
                  // Look for matching group in allGroupEntries
                  // Since allGroupEntries contains individual leave objects, we need to check if they share the same group
                  const matchingGroup = allGroupEntries.find(leave =>
                    leave.group === firstHalfEntry.group || leave.group === secondHalfEntry.group
                  );

                  if (matchingGroup) {
                    dayMods.specialGroupId = secondHalfEntry.group; // Use first half group as primary groupId
                  }
                }
              }
              dayMods.style = {
                '--leave-color': getLeaveColor(firstHalfEntry),
                '--leave-color-second': getLeaveColor(secondHalfEntry),
                background: `linear-gradient(to right, ${getLeaveColor(firstHalfEntry)} 50%, ${getLeaveColor(secondHalfEntry)} 50%) !important`,
                backgroundImage: `linear-gradient(to right, ${getLeaveColor(firstHalfEntry)} 50%, ${getLeaveColor(secondHalfEntry)} 50%) !important`,
                backgroundRepeat: 'no-repeat !important'
              };
            } else {
              // Different groups: Create a consolidated mod with both entries' information
              dayMods.classNames = ["halfLeftBook", "halfRightBook", "dynamic-color", "special-case-combined"];
              dayMods.id = firstHalfEntry.group; // Use first entry's group as primary
              dayMods.secondaryId = secondHalfEntry.group; // Store second entry's group for reference
              dayMods.firstHalfEntry = firstHalfEntry; // Store first entry data
              dayMods.secondaryHalfEntry = secondHalfEntry; // Store second entry data
              dayMods.isGroup = false;
              dayMods.style = {
                '--leave-color': getLeaveColor(firstHalfEntry),
                '--leave-color-second': getLeaveColor(secondHalfEntry),
                background: `linear-gradient(to right, ${getLeaveColor(firstHalfEntry)} 50%, ${getLeaveColor(secondHalfEntry)} 50%) !important`,
                backgroundImage: `linear-gradient(to right, ${getLeaveColor(firstHalfEntry)} 50%, ${getLeaveColor(secondHalfEntry)} 50%) !important`,
                backgroundRepeat: 'no-repeat !important'
              };

            }


            // Push the consolidated mod to the mods array
            mods.push(dayMods);
            continue;
          }
        }
        dayMods.isGroup = false;

        // Fallback: find any single leave that applies to this day
        const empLeave = employeeLeaves && employeeLeaves.find((a) => {
          const leaveStartDate = new Date(a.startDate);
          const leaveEndDate = new Date(a.endDate);

          // Reset time to start of day for comparison
          const currentDate = new Date(i.getFullYear(), i.getMonth(), i.getDate());
          const startDate = new Date(leaveStartDate.getFullYear(), leaveStartDate.getMonth(), leaveStartDate.getDate());
          const endDate = new Date(leaveEndDate.getFullYear(), leaveEndDate.getMonth(), leaveEndDate.getDate());

          // Check if current date falls within the leave range
          return currentDate >= startDate && currentDate <= endDate;
        });

        if (empLeave !== null && empLeave !== undefined) {

          // Get the color from empLeave.color
          const leaveColor = getLeaveColor(empLeave);
          const isAutoDeducted = !!(empLeave.isAutoDeducted || empLeave.IsAutoDeducted);
          const autoDeductedClass = isAutoDeducted ? "auto-deducted-leave" : null;
          const leaveTypeLabel = empLeave.leaveType || empLeave.LeaveType || "Leave";
          if (isAutoDeducted) {
            dayMods.title = `${leaveTypeLabel} (auto-deducted) — click to edit`;
          }

          // Check if it's a single day leave or part of a group
          const groupLeaves = employeeLeaves.filter(leave => leave.group === empLeave.group);
          if (groupLeaves.length === 1) {
            // Single Day Logic: Both Morning and Evening apply to the same day
            dayMods.isGroup = false; // Single day leave
            //console.log(`🔍 util.js Debug - Single day leave for ${i.format('YYYY-MM-DD')}, set isGroup = false`);
            if (empLeave.startingLeave === 0 && empLeave.endingLeave === 0) {
              // First half (0,0) - use halfLeftBook
              dayMods.classNames = ["halfLeftBook", "dynamic-color", autoDeductedClass].filter(Boolean);
              // Store the group ID for the first half, but don't set it as the main id
              // This allows the other half to remain selectable for adding new leaves
              dayMods.firstHalfGroupId = empLeave.group;
              //dayMods.id = empLeave.group;
              dayMods.style = {
                '--leave-color': leaveColor,
                background: `linear-gradient(to right, ${leaveColor} 50%, transparent 50%) !important`,
                backgroundImage: `linear-gradient(to right, ${leaveColor} 50%, transparent 50%) !important`
              };
              // Use the enhanced hover functionality from Day.js component
              // No need to add custom onMouseEnter/onMouseLeave here
              // The Day.js component will handle the hover effects using the group ID
            } else if (empLeave.startingLeave === 0 && empLeave.endingLeave === 1) {
              // Full day leave (0,1) - use longEvent
              dayMods.classNames = ["longEvent", "dynamic-color", autoDeductedClass].filter(Boolean);
              //dayMods.secondHalfGroupId = empLeave.group;
              dayMods.id = empLeave.group;
              //dayMods.id = empLeave.group;
              dayMods.style = {
                '--leave-color': leaveColor,
                backgroundColor: `${leaveColor} !important`,
                background: `${leaveColor} !important`
              };
              // Use the enhanced hover functionality from Day.js component
              // No need to add custom onMouseEnter/onMouseLeave here
              // The Day.js component will handle the hover effects using the group ID
            } else if (empLeave.startingLeave === 1 && empLeave.endingLeave === 1) {
              // Second half (1,1) - use halfRightBook
              dayMods.classNames = ["halfRightBook", "dynamic-color", autoDeductedClass].filter(Boolean);
              // Store the group ID for the second half, but don't set it as the main id
              // This allows the other half to remain selectable for adding new leaves
              dayMods.secondHalfGroupId = empLeave.group;
              //dayMods.id = empLeave.group;
              dayMods.style = {
                '--leave-color': leaveColor,
                background: `linear-gradient(to left, ${leaveColor} 50%, transparent 50%) !important`,
                backgroundImage: `linear-gradient(to left, ${leaveColor} 50%, transparent 50%) !important`
              };
              // Use the enhanced hover functionality from Day.js component
              // No need to add custom onMouseEnter/onMouseLeave here
              // The Day.js component will handle the hover effects using the group ID
            } else {
              // Default fallback - use longEvent
              dayMods.classNames = ["longEvent", "dynamic-color", autoDeductedClass].filter(Boolean);
              //dayMods.id = empLeave.group;
              dayMods.firstHalfGroupId = empLeave.group; // Store for first half only
              dayMods.style = {
                '--leave-color': leaveColor,
                backgroundColor: `${leaveColor} !important`,
                background: `${leaveColor} !important`
              };
              // Use the enhanced hover functionality from Day.js component
              // No need to add custom onMouseEnter/onMouseLeave here
              // The Day.js component will handle the hover effects using the group ID
            }
          } else {
            // Multi-day group: Apply backend-matching rules for each day
            dayMods.isGroup = true; // Multi-day group
            // console.log(`🔍 util.js Debug - Multi-day group for ${i.format('YYYY-MM-DD')}, set isGroup = true`);
            const sortedGroupLeaves = groupLeaves.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
            const groupStart = sortedGroupLeaves[0];
            const groupEnd = sortedGroupLeaves[sortedGroupLeaves.length - 1];
            const isFirstDay = empLeave.startDate === groupStart.startDate;
            const isLastDay = empLeave.startDate === groupEnd.startDate;

            let displayStart = 0;
            let displayEnd = 1;

            if (isFirstDay) {
              if (groupStart.startingLeave === 0) {
                // Morning: full day
                displayStart = 0;
                displayEnd = 1;
              } else if (groupStart.startingLeave === 1) {
                // Afternoon: second half
                displayStart = 1;
                displayEnd = 1;
              }
            } else if (isLastDay) {
              if (groupEnd.endingLeave === 0) {
                // LunchTime: first half
                displayStart = 0;
                displayEnd = 0;
              } else if (groupEnd.endingLeave === 1) {
                // EndOfDay: full day
                displayStart = 0;
                displayEnd = 1;
              }
            } else {
              // Middle days: full day
              displayStart = 0;
              displayEnd = 1;
            }
            // Apply styling based on displayStart/displayEnd
            if (displayStart === 0 && displayEnd === 0) {
              // First half
              dayMods.classNames = ["halfLeftBook", "dynamic-color", autoDeductedClass].filter(Boolean);
              // Don't set id for half-day leaves to allow adding to unoccupied half
              // dayMods.firstHalfGroupId = empLeave.group;
              dayMods.id = empLeave.group;
              dayMods.groupFirstHalf = true; // Store for first half only
              dayMods.groupSecondHalf = false;
              dayMods.leaveType = "first-half";
              dayMods.style = {
                '--leave-color': leaveColor,
                background: `linear-gradient(to right, ${leaveColor} 50%, transparent 50%) !important`,
                backgroundImage: `linear-gradient(to right, ${leaveColor} 50%, transparent 50%) !important`
              };

            } else if (displayStart === 1 && displayEnd === 1) {
              // Second half
              dayMods.classNames = ["halfRightBook", "dynamic-color", autoDeductedClass].filter(Boolean);
              // Don't set id for half-day leaves to allow adding to unoccupied half
              //dayMods.secondHalfGroupId = empLeave.group;
              dayMods.id = empLeave.group;
              dayMods.groupFirstHalf = false;
              dayMods.groupSecondHalf = true; // Store for second half only
              dayMods.leaveType = "second-half";
              dayMods.style = {
                '--leave-color': leaveColor,
                background: `linear-gradient(to left, ${leaveColor} 50%, transparent 50%) !important`,
                backgroundImage: `linear-gradient(to left, ${leaveColor} 50%, transparent 50%) !important`
              };
            } else if (displayStart === 0 && displayEnd === 1) {
              // Full day
              dayMods.classNames = ["longEvent", "dynamic-color", autoDeductedClass].filter(Boolean);
              //dayMods.firstHalfGroupId = empLeave.group;
              dayMods.id = empLeave.group;
              dayMods.groupFirstHalf = true;
              dayMods.groupSecondHalf = true;
              dayMods.leaveType = "full-day";
              dayMods.style = {
                '--leave-color': leaveColor,
                backgroundColor: `${leaveColor} !important`,
                background: `${leaveColor} !important`
              };

            } else {
              // Fallback: full day
              dayMods.classNames = ["longEvent", "dynamic-color", autoDeductedClass].filter(Boolean);
              dayMods.id = empLeave.group;
              dayMods.groupFirstHalf = true;
              dayMods.groupSecondHalf = true;
              dayMods.leaveType = "full-day-fallback";
              dayMods.style = {
                '--leave-color': leaveColor,
                backgroundColor: `${leaveColor} !important`,
                background: `${leaveColor} !important`
              };

            }

          }
        }
      }
    }
    // Ensure isGroup is always set before pushing
    if (dayMods.isGroup === undefined) {
      dayMods.isGroup = false;
    }

    mods.push(dayMods);
  }
  return mods;
}